import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, FormGroup, FormControlLabel, Checkbox, CircularProgress, Button } from '@mui/material';
import axios from 'axios';

// Este componente no renderiza nada directamente - trabaja con la capa del mapa ArcGIS
function PorticoLayer({ mapView, onPorticoClick }) {
  const [porticos, setPorticos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    freeflow: true,
    manual: true,
    mixto: true
  });

  const [markersLayer, setMarkersLayer] = useState(null);

  // Efecto para limpiar la capa cuando el componente se desmonta
  useEffect(() => {
    return () => {
      if (markersLayer) {
        try {
          markersLayer.removeAll();
          if (mapView && mapView.map) {
            mapView.map.remove(markersLayer);
          }
        } catch (err) {
          console.error('Error cleaning up markers layer:', err);
        }
      }
    };
  }, [markersLayer, mapView]);

  // Efecto para cargar datos cuando el componente se monta
  useEffect(() => {
    if (mapView) {
      loadPorticosData();
    }
  }, [mapView]);

  // Efecto para aplicar filtros cuando cambian
  useEffect(() => {
    if (markersLayer) {
      try {
        const allFiltersOff = !filters.freeflow && !filters.manual && !filters.mixto;

        // Si todos los filtros están desactivados, ocultar la capa completa
        markersLayer.visible = !allFiltersOff;

        if (!allFiltersOff) {
          markersLayer.graphics.forEach(graphic => {
            const porticoType = graphic.attributes.tipo;
            switch (porticoType) {
              case 'FREEFLOW':
                graphic.visible = filters.freeflow;
                break;
              case 'MANUAL':
                graphic.visible = filters.manual;
                break;
              case 'MIXTO':
                graphic.visible = filters.mixto;
                break;
              default:
                graphic.visible = false;
                break;
            }
          });
        }
      } catch (err) {
        console.error('Error applying filters:', err);
        setError('Error al aplicar filtros');
      }
    }
  }, [filters, markersLayer]);

  const createMarkersLayer = async (porticosData) => {
    if (!mapView || !porticosData.length) return;

    try {
      // Limpiar capa anterior si existe
      if (markersLayer) {
        markersLayer.removeAll();
        mapView.map.remove(markersLayer);
      }

      const [GraphicsLayer, Graphic, Point, SimpleMarkerSymbol] = await Promise.all([
        import('@arcgis/core/layers/GraphicsLayer').then(m => m.default),
        import('@arcgis/core/Graphic').then(m => m.default),
        import('@arcgis/core/geometry/Point').then(m => m.default),
        import('@arcgis/core/symbols/SimpleMarkerSymbol').then(m => m.default)
      ]);

      const layer = new GraphicsLayer({
        title: 'Pórticos',
        listMode: 'hide',
        visible: true
      });

      porticosData.forEach(portico => {
        if (!portico.latitud || !portico.longitud) return;

        let color, size;
        switch (portico.tipo) {
          case 'FREEFLOW':
            color = [51, 95, 185];
            size = 14;
            break;
          case 'TAG':
            color = [0, 168, 107];
            size = 12;
            break;
          case 'MANUAL':
            color = [128, 128, 128]; // Gray color for Manual
            size = 12;
            break;
          case 'MIXTO':
            color = [255, 140, 0]; // Orange color for Mixto
            size = 12;
            break;
          default:
            color = [128, 128, 128];
            size = 10;
        }

        const symbol = new SimpleMarkerSymbol({
          style: 'circle',
          color: color,
          size: size,
          outline: {
            color: [255, 255, 255],
            width: 1.5
          }
        });

        const point = new Point({
          longitude: portico.longitud,
          latitude: portico.latitud,
          spatialReference: { wkid: 4326 }
        });

        const graphic = new Graphic({
          geometry: point,
          symbol: symbol,
          attributes: {
            ...portico,
            porticoType: portico.tipo
          }
        });

        layer.add(graphic);
      });

      mapView.map.add(layer);
      setMarkersLayer(layer);

      // Configurar evento de click
      if (onPorticoClick) {
        mapView.on('click', (event) => {
          mapView.hitTest(event, {
            include: [layer]
          }).then(response => {
            if (response.results.length) {
              const graphic = response.results.find(r => r.graphic.layer === layer);
              if (graphic) {
                onPorticoClick(graphic.graphic.attributes);
              }
            }
          });
        });
      }

    } catch (err) {
      console.error('Error creating markers layer:', err);
      setError('Error al crear la capa de marcadores');
    }
  };

  const loadPorticosData = async () => {
    setLoading(true);
    setError(null);

    try {
      const porticosResponse = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/porticos/all`);
      if (!porticosResponse.data.success) {
        throw new Error('Error al obtener datos de pórticos');
      }

      const porticosData = [];
      porticosResponse.data.data.forEach(concesionariaGroup => {
        if (concesionariaGroup.porticos) {
          concesionariaGroup.porticos.forEach(portico => {
            porticosData.push({
              ...portico,
              concesionaria: concesionariaGroup.concesionaria,
              tipo_concesionaria: concesionariaGroup.tipo,
              cod_via: concesionariaGroup.cod_via,
              observaciones: concesionariaGroup.observaciones
            });
          });
        }
      });

      setPorticos(porticosData);
      await createMarkersLayer(porticosData);

    } catch (err) {
      console.error('Error loading porticos data:', err);
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (event) => {
    setFilters(prev => ({
      ...prev,
      [event.target.name]: event.target.checked
    }));
  };

  const handleRefresh = () => {
    loadPorticosData();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        width: '100%',
        maxWidth: 300,
        position: 'absolute',
        top: 230,
        right: 14,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.9)'
      }}
    >
      <Typography variant="h6" gutterBottom>
        Filtro de Pórticos
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {error && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.freeflow}
                  onChange={handleFilterChange}
                  name="freeflow"
                  color="primary"
                />
              }
              label="Free Flow"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.manual}
                  onChange={handleFilterChange}
                  name="manual"
                  color="default"
                />
              }
              label="Manual"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.mixto}
                  onChange={handleFilterChange}
                  name="mixto"
                  color="warning"
                />
              }
              label="Mixto"
            />
          </FormGroup>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total pórticos: {porticos.length}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleRefresh}
          >
            Actualizar datos
          </Button>
        </>
      )}
    </Paper>
  );
}

export default PorticoLayer; 